// Connexion temps réel (api/direct.js), ouverte pour tout l'onglet une fois connecté :
// signale la page affichée (utilisateurs en ligne) et transmet le flux de la page #activite à ses abonnés.

import { reactive, watch } from "vue";
import { filAriane } from "./filAriane";
import { clearCache } from "./listCache";

const DELAI_MAX = 30000;

let socket = null;
let actif = false;
let delai = 1000;
let attente = null;
let arreteSurveillance = null;
const abonnes = new Set(); // { onMessage, onEtat }

// Personnes en ligne (barre du haut), mises à jour par le serveur
export const enLigne = reactive({ personnes: [] });

// Nouvelle version de l'interface en place sur le serveur (bandeau de la barre du haut, src/app/views/sidebar.html)
// eslint-disable-next-line no-undef
const BUILD = typeof __BUILD_ID__ !== "undefined" ? __BUILD_ID__ : null;
export const nouvelleVersion = reactive({ disponible: false });

// Commandes passées « à traiter » pendant que l'onglet est ouvert (notifications, src/app/views/components/alertesATraiter.js)
export const alertesATraiter = reactive({ liste: [] });
let compteurAlertes = 0;

// Écrans abonnés aux modifications de données ({ sujets, recoit })
const ecouteurs = new Set();

function recoitModification(message) {
  // Listes gardées en mémoire (listCache) devenues fausses : on ne les réaffichera pas au retour
  if (message.sujets.includes("commandes")) clearCache();
  for (const e of ecouteurs) if (message.sujets.some((s) => e.sujets.includes(s))) e.recoit(message);
}

function ecranCourant() {
  const ecrans = window.location.hash.substring(1).split("/");
  return (ecrans[ecrans.length - 1] || "").split("&")[0];
}

function envoie(message) {
  if (socket && socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify(message));
}

const envoiePage = () => envoie({ type: "page", ecran: ecranCourant(), libelle: filAriane.libelle || "" });
const signaleEtat = (etat) => abonnes.forEach((a) => a.onEtat && a.onEtat(etat));

function ouvre() {
  signaleEtat("connexion");
  socket = new WebSocket(`${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/ws/direct`);
  socket.onopen = () => {
    delai = 1000;
    envoiePage();
    if (abonnes.size) envoie({ type: "abonne" });
    signaleEtat("connecte");
  };
  socket.onmessage = (event) => {
    let message;
    try {
      message = JSON.parse(event.data);
    } catch (e) {
      return;
    }
    if (message.type === "enLigne") enLigne.personnes = message.personnes;
    if (message.type === "version" && BUILD && message.build && message.build !== BUILD) nouvelleVersion.disponible = true;
    if (message.type === "modification") recoitModification(message);
    if (message.type === "aTraiter") alertesATraiter.liste.push({ ...message.commande, cle: ++compteurAlertes });
    abonnes.forEach((a) => a.onMessage(message));
  };
  socket.onclose = () => {
    if (!actif) return;
    signaleEtat("deconnecte");
    attente = setTimeout(ouvre, delai);
    delai = Math.min(delai * 2, DELAI_MAX);
  };
}

export function demarreDirect() {
  if (actif) return;
  actif = true;
  ouvre();
  window.addEventListener("hashchange", envoiePage);
  // Le libellé de la fiche (PO, nom...) arrive après le changement de page
  arreteSurveillance = watch(() => filAriane.libelle, envoiePage);
}

export function arreteDirect() {
  actif = false;
  clearTimeout(attente);
  window.removeEventListener("hashchange", envoiePage);
  if (arreteSurveillance) arreteSurveillance();
  if (socket) socket.close();
  socket = null;
  enLigne.personnes = [];
}

// Page #activite : reçoit la présence, l'historique puis les actions au fil de l'eau. Retourne la fonction de désabonnement.
export function abonneDirect(onMessage, onEtat) {
  const abonne = { onMessage, onEtat };
  abonnes.add(abonne);
  if (socket && socket.readyState === WebSocket.OPEN) {
    envoie({ type: "abonne" });
    if (onEtat) onEtat("connecte");
  } else if (onEtat) {
    onEtat("connexion");
  }
  return () => {
    abonnes.delete(abonne);
    if (!abonnes.size) envoie({ type: "desabonne" });
  };
}

// Comme ecouteModifications, pour un élément toujours affiché (pastille du menu) : sans arrêt au changement d'écran
export function ecouteModificationsGlobales(sujets, surModification) {
  let attente = null;
  const ecouteur = {
    sujets,
    recoit(message) {
      clearTimeout(attente);
      attente = setTimeout(() => surModification(message), 800);
    },
  };
  ecouteurs.add(ecouteur);
  return () => {
    ecouteurs.delete(ecouteur);
    clearTimeout(attente);
  };
}

// Un écran recharge ce qu'il affiche quand ses données changent (action d'un collègue, import...).
// Actif tant que l'écran est affiché ; les modifications rapprochées (une affectation en écrit plusieurs)
// sont regroupées en un seul appel, avec la dernière reçue. Retourne la fonction d'arrêt.
export function ecouteModifications(ecran, sujets, surModification) {
  let attente = null;
  const ecouteur = {
    sujets,
    recoit(message) {
      clearTimeout(attente);
      attente = setTimeout(() => surModification(message), 800);
    },
  };
  const arrete = () => {
    ecouteurs.delete(ecouteur);
    clearTimeout(attente);
    window.removeEventListener("hashchange", surNavigation);
  };
  const surNavigation = () => {
    if (ecranCourant() !== ecran) arrete();
  };
  ecouteurs.add(ecouteur);
  window.addEventListener("hashchange", surNavigation);
  return arrete;
}

